import { createContext, useContext, useEffect, useState } from 'react';
import { de as deDateFormatLocale, enUS as enDateFormatLocale } from 'date-fns/locale';
import translations from './translations';
import registerTYPO3ValidationMessagesDe from './registerTYPO3ValidationMessagesDe';
import registerTYPO3ValidationMessagesEn from './registerTYPO3ValidationMessagesEn';

const applicationAvailableLocales = Object.keys(translations);
const dateFormatLocales = {
    de: deDateFormatLocale,
    en: enDateFormatLocale,
};

function findDefaultLocale(availableLocales) {
    // window.navigator.languages is not available in IE 11 causing an error, which is why we check for it here.
    // IE 11 users will use the first available locale per default and have to switch manually then.
    if (window.navigator.languages) {
        for (let language of window.navigator.languages) {
            // Support language codes with a region code (e.g. en-US). We only use the language part.
            if (language.includes('-')) {
                // eslint-disable-next-line prefer-destructuring
                language = language.split('-')[0];
            }

            if (availableLocales.includes(language)) {
                return language;
            }
        }
    }

    // If no user language matched, use the first available locale
    return availableLocales[0];
}

const TranslationContext = createContext({
    availableLocales: applicationAvailableLocales,
    locale: findDefaultLocale(applicationAvailableLocales),
    // eslint-disable-next-line no-unused-vars
    setLocale: (locale) => null,
});

export const useTranslationContext = () => useContext(TranslationContext);

export const useDateFormatLocale = () => {
    const { locale } = useTranslationContext();

    return dateFormatLocales[locale] || deDateFormatLocale;
};

export const useTranslations = () => {
    const { locale } = useTranslationContext();

    return translations[locale];
};

export const useTranslation = (translationKey, parameters) => {
    const translation = useTranslations()[translationKey];
    const translationType = typeof translation;

    switch (translationType) {
        case 'string':
            return translation;
        case 'function':
            return translation(parameters);
        case 'undefined':
            throw new Error(`Could not find translation with key ${translationKey}`);
        default:
            throw new Error(`Unsupported type for key ${translationKey}: ${translationType}`);
    }
};

export const Translation = ({ translationKey, parameters = {} }) => useTranslation(translationKey, parameters);

const TranslationProvider = ({ availableLocales = applicationAvailableLocales, children }) => {
    const [locale, setLocale] = useState(findDefaultLocale(availableLocales));

    // Register SBK components TYPO3 validation messages. The register functions completely override the existing
    // TYPO3 validation messages, so no need for a cleanup function.
    useEffect(() => {
        if (locale === 'de') {
            registerTYPO3ValidationMessagesDe();
        }
        if (locale === 'en') {
            registerTYPO3ValidationMessagesEn();
        }
    }, [locale]);

    return (
        // eslint-disable-next-line react/jsx-no-constructed-context-values
        <TranslationContext.Provider value={{ availableLocales, locale, setLocale }}>
            {children}
        </TranslationContext.Provider>
    );
};

export default TranslationProvider;
